import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChatCircleText, ThumbsUp, BookmarkSimple, Sparkle, Check, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { useNotifications } from "../contexts/NotificationsContext";

/**
 * The dedicated Notifications page (`/notifications`).
 *
 * Lists every engagement event on the current user's posts, newest first,
 * with a "Mark all as read" CTA at the top. Clicking a row jumps to the
 * post AND silently marks just that row as read (so the user doesn't have
 * to use "mark all" to clear them one at a time).
 */
const TYPE_META = {
    like: {
        Icon: ThumbsUp,
        tone: "text-emerald-500",
        action: "liked your idea",
    },
    save: {
        Icon: BookmarkSimple,
        tone: "text-sky-500",
        action: "saved your idea",
    },
    comment: {
        Icon: ChatCircleText,
        tone: "text-[#6C63FF]",
        action: "commented on your idea",
    },
    like_milestone: {
        Icon: Sparkle,
        tone: "text-[#FF8A00]",
        action: "Your idea hit a milestone",
    },
};

function timeAgo(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
}

export default function Notifications() {
    const navigate = useNavigate();
    const { refresh: refreshBadges } = useNotifications();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 100 } });
            setItems(data?.items || []);
        } catch (_e) {
            toast.error("Could not load notifications");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const markAllRead = async () => {
        if (marking) return;
        setMarking(true);
        try {
            const { data } = await api.post("/notifications/read", {});
            toast.success(data?.marked > 0 ? `Marked ${data.marked} as read` : "All caught up");
            setItems((rows) => rows.map((r) => ({ ...r, read: true })));
            refreshBadges();
        } catch (_e) {
            toast.error("Could not mark all as read");
        } finally {
            setMarking(false);
        }
    };

    const openNotification = async (n) => {
        if (!n.read) {
            // Optimistic: flip read locally, then quietly tell the server.
            setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
            api.post("/notifications/read", { ids: [n.id] })
                .then(refreshBadges)
                .catch(() => {});
        }
        if (n.post_id) navigate(`/post/${n.post_id}`);
    };

    const dismiss = async (n, e) => {
        e.stopPropagation();
        const prev = items;
        setItems((rows) => rows.filter((r) => r.id !== n.id));
        try {
            await api.delete(`/notifications/${n.id}`);
            refreshBadges();
        } catch (_e) {
            setItems(prev);
            toast.error("Could not delete");
        }
    };

    const unreadCount = items.filter((i) => !i.read).length;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="notifications-page">
            <div className="flex items-end justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
                        <Bell size={26} weight="fill" className="text-[#6C63FF]" />
                        Notifications
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Engagement on your ideas — likes, saves, and comments.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={markAllRead}
                    disabled={marking || unreadCount === 0}
                    data-testid="mark-all-read-btn"
                >
                    <Check size={14} weight="bold" />
                    Mark all as read
                </Button>
            </div>

            {loading && (
                <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                    Loading…
                </div>
            )}

            {!loading && items.length === 0 && (
                <div
                    className="rounded-xl border border-dashed border-border bg-card p-10 text-center"
                    data-testid="notifications-empty"
                >
                    <Bell size={36} className="mx-auto text-muted-foreground/40" />
                    <p className="mt-3 font-semibold">No notifications yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                        When someone likes, saves or comments on your ideas, you'll see it here.
                    </p>
                </div>
            )}

            <ul className="space-y-2" data-testid="notifications-list">
                {items.map((n) => {
                    const meta = TYPE_META[n.type] || TYPE_META.comment;
                    const { Icon, tone, action } = meta;
                    const headline = n.type === "like_milestone"
                        ? `Your idea hit ${n.milestone} likes!`
                        : `${n.actor_name} ${action}`;
                    return (
                        <li
                            key={n.id}
                            onClick={() => openNotification(n)}
                            data-testid={`notification-${n.id}`}
                            data-unread={!n.read || undefined}
                            className={`group cursor-pointer flex items-start gap-3 p-4 rounded-xl border transition-colors
                                ${n.read
                                    ? "border-border bg-card hover:bg-muted/40"
                                    : "border-[#6C63FF]/30 bg-[#6C63FF]/5 hover:bg-[#6C63FF]/10"
                                }`}
                        >
                            <span className={`shrink-0 mt-0.5 ${tone}`}>
                                <Icon size={20} weight="fill" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm">
                                    <span className="font-bold">{headline}</span>
                                </p>
                                {n.post_title && (
                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                        {n.post_title}
                                    </p>
                                )}
                                {n.comment_preview && (
                                    <p className="text-xs italic text-foreground/70 mt-1 line-clamp-2">
                                        “{n.comment_preview}”
                                    </p>
                                )}
                                <p className="text-[11px] text-muted-foreground mt-1.5">
                                    {timeAgo(n.created_at)}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={(e) => dismiss(n, e)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-rose-500/10 text-muted-foreground hover:text-rose-500"
                                data-testid={`dismiss-${n.id}`}
                                aria-label="Dismiss"
                            >
                                <Trash size={14} />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
